/**
 * D3 Bar implementation. Imperative D3 + React for measurement; uses d3-scale
 * directly and renders SVG via JSX (no DOM mutation by d3 — keeps React's
 * reconciliation owning the tree).
 *
 * Visually equivalent to the Visx and Recharts impls. Honest comparison
 * point — proves D3 can drive the same dashboard surface as Visx (Visx is
 * d3-scale + d3-shape wrapped in React; D3 here is the same primitives
 * unwrapped).
 */

import { useMemo, useState, useRef, useEffect } from 'react';
import { scaleBand, scaleLinear } from 'd3-scale';
import type { BarChartProps, CommonChartProps } from '../types';
import { chartTheme } from '../theme';
import { DrillAwareTooltip } from '@/components/analytics/DrillAwareTooltip';

type Props = BarChartProps & CommonChartProps;

const MARGIN = { top: 12, right: 16, bottom: 36, left: 48 };

function useMeasuredRef<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  useEffect(() => {
    if (!ref.current) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setSize({ width: entry.contentRect.width, height: entry.contentRect.height });
      }
    });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return [ref, size] as const;
}

export function D3Bar({ data }: Props): JSX.Element {
  const [ref, { width, height }] = useMeasuredRef<HTMLDivElement>();
  const theme = chartTheme('d3').theme;
  const palette = theme.categoricalPalette;
  const [hover, setHover] = useState<{ x: number; y: number; index: number } | null>(null);

  const innerW = Math.max(0, width - MARGIN.left - MARGIN.right);
  const innerH = Math.max(0, height - MARGIN.top - MARGIN.bottom);

  const { xScale, yScale, yTicks } = useMemo(() => {
    const xs = scaleBand<string>().domain(data.map((d) => d.label)).range([0, innerW]).padding(0.25);
    const yMax = Math.max(0, ...data.map((d) => d.value)) || 1;
    const ys = scaleLinear().domain([0, yMax]).range([innerH, 0]).nice();
    return { xScale: xs, yScale: ys, yTicks: ys.ticks(5) };
  }, [data, innerW, innerH]);

  return (
    <div ref={ref} style={{ position: 'relative', width: '100%', height: '100%' }}>
      {width > 0 && height > 0 && (
        <svg width={width} height={height}>
          <g transform={`translate(${MARGIN.left},${MARGIN.top})`}>
            {/* Grid + Y axis */}
            {yTicks.map((tick) => (
              <g key={tick} transform={`translate(0,${yScale(tick)})`}>
                <line x1={0} x2={innerW} stroke={theme.gridColor} strokeDasharray="3 3" />
                <text x={-4} y={0} dy="0.32em" fontSize={11} textAnchor="end" fill={theme.textColor}>
                  {tick}
                </text>
              </g>
            ))}
            {/* X axis */}
            <line x1={0} y1={innerH} x2={innerW} y2={innerH} stroke={theme.gridColor} />
            {data.map((d) => (
              <text
                key={`x-${d.label}`}
                x={(xScale(d.label) ?? 0) + xScale.bandwidth() / 2}
                y={innerH + 18}
                fontSize={11}
                textAnchor="middle"
                fill={theme.textColor}
              >
                {d.label}
              </text>
            ))}
            {/* Bars */}
            {data.map((d, i) => {
              const x = xScale(d.label) ?? 0;
              const y = yScale(d.value);
              const barH = innerH - y;
              const fill = (d.color as string) ?? palette[i % palette.length];
              return (
                <rect
                  key={`${d.label}-${i}`}
                  x={x}
                  y={y}
                  width={xScale.bandwidth()}
                  height={barH}
                  fill={fill}
                  rx={4}
                  onMouseMove={(e) => {
                    const rect = (e.currentTarget.ownerSVGElement as SVGSVGElement | null)?.getBoundingClientRect();
                    setHover({ x: e.clientX - (rect?.left ?? 0), y: e.clientY - (rect?.top ?? 0), index: i });
                  }}
                  onMouseLeave={() => setHover(null)}
                />
              );
            })}
          </g>
        </svg>
      )}
      {hover && (
        <div
          style={{
            position: 'absolute',
            left: Math.min(hover.x + 12, width - 200),
            top: Math.max(hover.y - 40, 0),
            pointerEvents: 'none',
          }}
        >
          <DrillAwareTooltip
            rows={[
              {
                color: (data[hover.index].color as string) ?? palette[hover.index % palette.length],
                label: data[hover.index].label,
                value: data[hover.index].value,
              },
            ]}
          />
        </div>
      )}
    </div>
  );
}
